/**
 * EmailVerificationBanner — persistent reminder shown when email is not verified.
 * Allows resending the verification email.
 */

import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, getNetworkErrorMessage } from '../lib/api';
import { useToast } from './Toast';

export function EmailVerificationBanner() {
  const { user, refreshUser } = useAuth();
  const { addToast } = useToast();
  const [isSending, setIsSending] = useState(false);

  if (!user || user.emailVerified) return null;

  const handleResend = async () => {
    setIsSending(true);
    try {
      await apiFetch('/v1/auth/resend-verification', { method: 'POST' });
      addToast({ type: 'success', message: 'Verification email sent. Check your inbox.' });
      await refreshUser();
    } catch (err) {
      addToast({
        type: 'error',
        message: getNetworkErrorMessage(err, 'Failed to send verification email.'),
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      className="mb-6 flex items-center gap-3 rounded-xl border border-yellow-500/20 bg-yellow-500/10 px-4 py-3"
      role="alert"
    >
      <span className="text-yellow-400">⚠️</span>
      <p className="flex-1 text-sm text-yellow-300">
        Your email is not verified. Verify it to unlock API key management and team features.
      </p>
      <button
        type="button"
        onClick={handleResend}
        disabled={isSending}
        className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-1.5 text-xs font-semibold text-yellow-300 hover:bg-yellow-500/20 disabled:opacity-50"
      >
        {isSending ? 'Sending...' : 'Resend'}
      </button>
    </div>
  );
}
